import { TenantRoles } from '@sal/shared/domain/value-objects';

export type TenantHostnameRole = TenantRoles.SAG | TenantRoles.SE | TenantRoles.SHS;
type BrandTenantRole = TenantRoles.SE | TenantRoles.SHS;

// Single source of truth for hostname patterns, shared by ThemeService (presentation)
// and TenantAuthorizationService (access control) so the two can't drift out of sync.
const BRAND_TENANT_PREFIXES: Record<BrandTenantRole, string> = {
    [TenantRoles.SHS]: 'healthineers.',
    [TenantRoles.SE]: 'energy.'
};

const brandTenantHostnameRules: { tenantRole: BrandTenantRole; matches: (hostname: string) => boolean }[] = [
    {
        tenantRole: TenantRoles.SHS,
        matches: hostname => hostname.includes(BRAND_TENANT_PREFIXES[TenantRoles.SHS])
    },
    {
        tenantRole: TenantRoles.SE,
        matches: hostname => hostname.includes(BRAND_TENANT_PREFIXES[TenantRoles.SE])
    }
];

// Explicit allow-list so an unrecognized hostname resolves to `null` (deny) in
// TenantAuthorizationService instead of silently defaulting to SAG.
const isSagHostname = (hostname: string): boolean =>
    hostname.includes('sal.siemens.cloud') || hostname === 'localhost' || hostname === '127.0.0.1';

function matchBrandTenantHostname(hostname: string): BrandTenantRole | null {
    return brandTenantHostnameRules.find(rule => rule.matches(hostname))?.tenantRole ?? null;
}

export function matchTenantHostname(hostname: string): TenantHostnameRole | null {
    const normalizedHostname = hostname.toLowerCase();
    return matchBrandTenantHostname(normalizedHostname) ?? (isSagHostname(normalizedHostname) ? TenantRoles.SAG : null);
}

// Builds the hostname for another tenant by swapping the current brand prefix,
// so cross-tenant redirects keep the same environment (dev/local/prod).
export function buildTenantHostname(hostname: string, tenantRole: TenantHostnameRole): string {
    const normalizedHostname = hostname.toLowerCase();
    const existingBrandPrefix = Object.values(BRAND_TENANT_PREFIXES).find(prefix =>
        normalizedHostname.includes(prefix)
    );
    const baseHostname = Object.values(BRAND_TENANT_PREFIXES).reduce(
        (currentHostname, prefix) => currentHostname.replace(`${prefix}`, ''),
        normalizedHostname
    );

    if (tenantRole === TenantRoles.SAG) {
        return baseHostname;
    }

    if (!existingBrandPrefix) {
        return BRAND_TENANT_PREFIXES[tenantRole] + baseHostname;
    }

    const prefixPosition = normalizedHostname.indexOf(existingBrandPrefix);
    return `${baseHostname.slice(0, prefixPosition)}${BRAND_TENANT_PREFIXES[tenantRole]}${baseHostname.slice(prefixPosition)}`;
}
